'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import type { StatusDotVariant } from '@/ui/status-dot/StatusDot'
import { StatusDot, StatusDotSkeleton } from '@/ui/status-dot/StatusDot'
import { StatusLine } from '@/ui/status-line/StatusLine'
import { gql } from '@gql-generated/gql'
import type {
  FullOpeningHourFragment,
  PointOfSaleOpeningStatus_ExceptionalDatesFragment,
} from '@gql-generated/graphql'
import { useEffect, useState } from 'react'
import type { OpeningStatus } from '../../services/pointOfSale.mapping'
import { mapDayOpeningHoursToOpeningStatus } from '../../services/pointOfSale.mapping'
import { PointOfSaleOpeningStatusSkeleton } from '../point-of-sale-status-card/PointOfSaleStatusCardSkeleton'

export const openingStatusCssClasses: Record<OpeningStatus['status'], string> = {
  open: 'font-bold text-success-fg',
  exceptionally_open: 'font-bold text-success-fg',
  closed: 'font-bold text-error-fg',
  exceptionally_closed: 'font-bold text-error-fg',
  closing_soon: 'font-bold text-warning-fg',
  not_business_day: '',
}

const openingStatusDotVariantStatus: Record<OpeningStatus['status'], StatusDotVariant> = {
  open: 'success',
  exceptionally_open: 'success',
  closed: 'error',
  exceptionally_closed: 'error',
  closing_soon: 'warning',
  not_business_day: 'error',
}

gql(/* GraphQL */ `
  fragment PointOfSaleOpeningStatus_ExceptionalDates on ExceptionalDate {
    date
    isClosed
  }
`)

type PointOfSaleOpeningStatusProps = {
  openingHours: FullOpeningHourFragment | FullOpeningHourFragment[] | null
  exceptionalDates: PointOfSaleOpeningStatus_ExceptionalDatesFragment[]
  notificationOnly?: boolean
  className?: string
}

export function PointOfSaleOpeningStatus({
  openingHours,
  exceptionalDates,
  notificationOnly = false,
  className,
}: PointOfSaleOpeningStatusProps): React.ReactElement | null {
  const { t } = useTranslation(['pointOfSale'])

  const [openingStatus, setOpeningStatus] = useState<OpeningStatus | null>(null)

  useEffect(() => {
    if (!openingHours || (Array.isArray(openingHours) && openingHours.length === 0)) {
      setOpeningStatus(null)

      return
    }

    const status = mapDayOpeningHoursToOpeningStatus(openingHours, exceptionalDates)
    setOpeningStatus(status)
  }, [openingHours, exceptionalDates])

  if (!openingHours || (Array.isArray(openingHours) && openingHours.length === 0)) {
    return null
  }

  if (!openingStatus) {
    return notificationOnly ? (
      <div className={twMerge('inline-flex rounded-full', className)}>
        <StatusDotSkeleton />
      </div>
    ) : (
      <PointOfSaleOpeningStatusSkeleton />
    )
  }

  return notificationOnly ? (
    <div className={twMerge('inline-flex rounded-full', className)}>
      <StatusDot variant={openingStatusDotVariantStatus[openingStatus.status]} />
    </div>
  ) : (
    <StatusLine variant={openingStatusDotVariantStatus[openingStatus.status]}>
      <span className={openingStatusCssClasses[openingStatus.status]}>
        {t(`pointOfSale:openingStatus.${openingStatus.status}`)}
      </span>
      {openingStatus.status === 'closed' && openingStatus.reopeningAt
        ? ` ${t('pointOfSale:openingHours.openAt', { hour: openingStatus.reopeningAt })}`
        : null}
    </StatusLine>
  )
}
